/**
 * RoamerNetworkModule.ts — Module #8: the Roamer Network™.
 *
 * A platform module (NOT a core engine): location-aware professional
 * networking — profile, peer discovery, connections, meetups,
 * collaboration, local groups, verification indicators, community
 * reputation, and privacy. Module pattern:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound ONLY via Core bus events + manual entry:
 *    - LocationChanged ({ location: {...} } wrapped, flat fallback) →
 *      currentBase (stored locally; EXPOSURE is gated by privacy).
 *    - CommunityUpdated is fusion metadata-only — refresh signal, never data.
 *    - MissionCompleted → missionParticipation reputation counter.
 *    - NotificationSent kind 'achievementUnlocked' (from other modules,
 *      e.g. Passport) → mirrored into profile achievements. The module
 *      MUST ignore its own NotificationSent publications (loop guard).
 *  • Outbound: the Core event union is fixed, so the brief's
 *    ConnectionCreated/MeetupCreated/GroupJoined/CommunityContribution
 *    publish as NotificationSent kinds 'connectionCreated', 'meetupCreated',
 *    'groupJoined', 'communityContribution' — and ONLY when privacy.visible
 *    is on (everything is opt-in; local recording still works when off).
 *  • Honesty: peers exist only as recorded PeerProfile records (a future
 *    backend syncs real peers); discovery filters exclude unknowns under
 *    demanded floors; suggestions carry recorded-overlap basis; trust score
 *    needs ≥3 recorded signals; group member counts are recorded-or-null;
 *    verification stays 'unverified' until a real future process runs.
 *  • Privacy: EVERYTHING off by default. allowConnectionRequests gates
 *    incoming requests; visible gates bus publications and the export
 *    profile; shareCurrentBase / shareProfessionalDetails strip fields.
 *  • Persistence nomad:network:v1 with record-by-record sanitization, caps,
 *    deep clones at read boundaries; export + deleteAll; injectable
 *    timestamps; handler errors never break the bus.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  COLLABORATION_KINDS,
  DEFAULT_NETWORK_PRIVACY,
  DEFAULT_NETWORK_PROFILE,
  MEETUP_KINDS,
  VERIFICATION_INDICATOR_KINDS,
  type Collaboration,
  type CollaborationKind,
  type CommunityReputation,
  type ConnectionRequest,
  type ConnectionSuggestion,
  type DiscoveryFilters,
  type LocalGroup,
  type Meetup,
  type MeetupKind,
  type NetworkAvailability,
  type NetworkConnection,
  type NetworkExport,
  type NetworkPrivacySettings,
  type NetworkProfile,
  type PeerProfile,
  type Recommendation,
  type VerificationIndicator,
  type VerificationIndicatorKind,
} from './models';
import { computeCommunityTrust, discoverPeers, suggestConnections } from './discovery';

const STORAGE_KEY = 'nomad:network:v1';
const MODULE_ID = 'roamer-network-module';
const MAX_PEERS = 1000;
const MAX_REQUESTS = 500;
const MAX_MEETUPS = 300;
const MAX_COLLABORATIONS = 200;
const MAX_GROUPS = 200;
const MAX_INDICATORS = 50;
const MAX_RECOMMENDATIONS = 200;
const MAX_ACHIEVEMENTS = 200;

const AVAILABILITIES: NetworkAvailability[] = ['openToMeet', 'busy', 'headsDown', 'unknown'];

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function asNonNegativeInt(v: unknown): number {
  const n = asNumberOrNull(v);
  return n != null && n >= 0 ? Math.floor(n) : 0;
}
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

interface PersistedState {
  profile: NetworkProfile;
  privacy: NetworkPrivacySettings;
  selfLatitude: number | null;
  selfLongitude: number | null;
  peers: PeerProfile[];
  connections: NetworkConnection[];
  requests: ConnectionRequest[];
  meetups: Meetup[];
  collaborations: Collaboration[];
  groups: LocalGroup[];
  indicators: VerificationIndicator[];
  recommendations: Recommendation[];
  helpfulReports: number;
  contributions: number;
  missionParticipation: number;
  refreshSignalCount: number;
}

export type NewPeerInput = Omit<PeerProfile, 'id' | 'addedAt'>;
export type NewMeetupInput = Omit<Meetup, 'id' | 'createdAt' | 'attended'>;
export type NewCollaborationInput = Omit<Collaboration, 'id' | 'createdAt'>;
export type NewGroupInput = Omit<LocalGroup, 'id' | 'addedAt' | 'joined' | 'joinedAt'>;

export class RoamerNetworkModule {
  private profile: NetworkProfile = structuredClone(DEFAULT_NETWORK_PROFILE);
  private privacy: NetworkPrivacySettings = { ...DEFAULT_NETWORK_PRIVACY };
  private selfLatitude: number | null = null;
  private selfLongitude: number | null = null;
  private peers: PeerProfile[] = [];
  private connections: NetworkConnection[] = [];
  private requests: ConnectionRequest[] = [];
  private meetups: Meetup[] = [];
  private collaborations: Collaboration[] = [];
  private groups: LocalGroup[] = [];
  private indicators: VerificationIndicator[] = [];
  private recommendations: Recommendation[] = [];
  private helpfulReports = 0;
  private contributions = 0;
  private missionParticipation = 0;
  private refreshSignalCount = 0;
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration ────────────────────────────────────────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Network™',
      version: '1.0.0',
      capabilities: ['network-profile', 'peer-discovery', 'connections', 'meetups', 'local-groups', 'reputation'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const safe = (handler: (e: CoreEvent) => void) => (e: CoreEvent) => {
      try {
        handler(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    };
    this.unsubscribes.push(core.on('LocationChanged', safe((e) => this.onLocationChanged(e)), 'module:network'));
    this.unsubscribes.push(core.on('CommunityUpdated', safe(() => this.onRefreshSignal()), 'module:network'));
    this.unsubscribes.push(core.on('MissionCompleted', safe(() => this.onMissionCompleted()), 'module:network'));
    this.unsubscribes.push(core.on('NotificationSent', safe((e) => this.onNotification(e)), 'module:network'));
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  isAttached(): boolean {
    return this.core != null;
  }

  /** All spec publications are opt-in: silent unless privacy.visible. */
  private publishNetworkEvent(kind: string, payload: Record<string, unknown>, at: number): void {
    if (!this.privacy.visible) return;
    try {
      // Spread FIRST so a payload field can never clobber the notification kind.
      this.core?.publish('NotificationSent', MODULE_ID, { ...payload, kind }, at);
    } catch {
      /* best-effort */
    }
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city);
    const lat = asNumberOrNull(p.latitude);
    const lon = asNumberOrNull(p.longitude);
    const cityChanged = city != null && city !== this.profile.currentBase;
    const coordsChanged = (lat != null && lat !== this.selfLatitude) ||
      (lon != null && lon !== this.selfLongitude);
    if (!cityChanged && !coordsChanged) return;
    if (city != null) this.profile.currentBase = city;
    // Coordinates refresh independently of city changes (distance discovery).
    if (lat != null) this.selfLatitude = lat;
    if (lon != null) this.selfLongitude = lon;
    this.afterUpdate();
  }

  private onRefreshSignal(): void {
    // Fusion events carry metadata only — a refresh signal, never data.
    this.refreshSignalCount += 1;
    this.afterUpdate();
  }

  private onMissionCompleted(): void {
    this.missionParticipation += 1;
    this.afterUpdate();
  }

  private onNotification(e: CoreEvent): void {
    if (e.source === MODULE_ID) return; // loop guard — never consume our own events
    const p = (e.payload ?? {}) as Record<string, unknown>;
    if (p.kind !== 'achievementUnlocked') return;
    const title = asString(p.title) ?? asString(p.achievementId);
    if (!title || this.profile.achievements.includes(title)) return;
    this.profile.achievements.push(title);
    if (this.profile.achievements.length > MAX_ACHIEVEMENTS) {
      this.profile.achievements = this.profile.achievements.slice(-MAX_ACHIEVEMENTS);
    }
    this.afterUpdate();
  }

  getRefreshSignalCount(): number {
    return this.refreshSignalCount;
  }

  // ── Profile ─────────────────────────────────────────────────────────────────

  getProfile(): NetworkProfile {
    return structuredClone(this.profile);
  }

  updateProfile(patch: Partial<Omit<NetworkProfile, 'verificationStatus' | 'achievements'>>): NetworkProfile {
    // verificationStatus is future-process-owned; achievements are bus-fed.
    // Runtime guard too — the type signature alone can be bypassed from JS.
    const safe = { ...patch } as Record<string, unknown>;
    delete safe.verificationStatus;
    delete safe.achievements;
    this.profile = { ...this.profile, ...(safe as Partial<NetworkProfile>) };
    this.afterUpdate();
    return this.getProfile();
  }

  // ── Peers & discovery ───────────────────────────────────────────────────────

  addPeer(input: NewPeerInput, at: number = Date.now()): PeerProfile | null {
    if (!asString(input.displayName)) return null;
    if (!AVAILABILITIES.includes(input.availability)) return null;
    const peer: PeerProfile = {
      ...input,
      displayName: input.displayName.trim(),
      professionalTitle: asString(input.professionalTitle),
      city: asString(input.city),
      country: asString(input.country),
      latitude: asNumberOrNull(input.latitude),
      longitude: asNumberOrNull(input.longitude),
      languages: asStringArray(input.languages),
      skills: asStringArray(input.skills),
      industries: asStringArray(input.industries),
      interests: asStringArray(input.interests),
      remoteExperienceYears: asNumberOrNull(input.remoteExperienceYears),
      verified: input.verified === true,
      id: `peer-${at}-${this.peers.length}`,
      addedAt: at,
    };
    this.peers.push(peer);
    if (this.peers.length > MAX_PEERS) this.peers = this.peers.slice(-MAX_PEERS);
    this.afterUpdate();
    return structuredClone(peer);
  }

  getPeers(): PeerProfile[] {
    return structuredClone(this.peers);
  }

  discover(filters: DiscoveryFilters = {}): PeerProfile[] {
    return structuredClone(discoverPeers(this.peers, filters, {
      interests: this.profile.interests,
      latitude: this.selfLatitude,
      longitude: this.selfLongitude,
    }));
  }

  // ── Connections ─────────────────────────────────────────────────────────────

  /** Incoming requests honour allowConnectionRequests; outgoing are always allowed. */
  createConnectionRequest(
    peerId: string,
    direction: 'incoming' | 'outgoing',
    message: string = '',
    at: number = Date.now(),
  ): ConnectionRequest | null {
    if (!this.peers.some((p) => p.id === peerId)) return null;
    if (direction === 'incoming' && !this.privacy.allowConnectionRequests) return null;
    if (this.connections.some((c) => c.peerId === peerId)) return null;
    if (this.requests.some((r) => r.peerId === peerId && r.status === 'pending')) return null;
    const request: ConnectionRequest = {
      id: `req-${at}-${this.requests.length}`,
      peerId,
      direction,
      message: message ?? '',
      status: 'pending',
      createdAt: at,
      resolvedAt: null,
    };
    this.requests.push(request);
    if (this.requests.length > MAX_REQUESTS) this.requests = this.requests.slice(-MAX_REQUESTS);
    this.afterUpdate();
    return structuredClone(request);
  }

  resolveConnectionRequest(id: string, accept: boolean, at: number = Date.now()): boolean {
    const request = this.requests.find((r) => r.id === id);
    if (!request || request.status !== 'pending') return false;
    request.status = accept ? 'accepted' : 'declined';
    request.resolvedAt = at;
    if (accept && !this.connections.some((c) => c.peerId === request.peerId)) {
      this.connections.push({
        peerId: request.peerId,
        favorite: false,
        follower: false,
        notes: '',
        connectedAt: at,
      });
      this.publishNetworkEvent('connectionCreated', { peerId: request.peerId }, at);
    }
    this.afterUpdate();
    return true;
  }

  updateConnection(peerId: string, patch: Partial<Pick<NetworkConnection, 'favorite' | 'follower' | 'notes'>>): boolean {
    const connection = this.connections.find((c) => c.peerId === peerId);
    if (!connection) return false;
    Object.assign(connection, patch);
    this.afterUpdate();
    return true;
  }

  getConnections(filter?: { favoritesOnly?: boolean }): NetworkConnection[] {
    let list = this.connections;
    if (filter?.favoritesOnly) list = list.filter((c) => c.favorite);
    return structuredClone(list);
  }

  getRecentConnections(limit: number = 5): NetworkConnection[] {
    return structuredClone(
      [...this.connections].sort((a, b) => b.connectedAt - a.connectedAt).slice(0, limit),
    );
  }

  getConnectionRequests(status?: ConnectionRequest['status']): ConnectionRequest[] {
    const list = status ? this.requests.filter((r) => r.status === status) : this.requests;
    return structuredClone(list);
  }

  getSuggestedConnections(): ConnectionSuggestion[] {
    return structuredClone(suggestConnections(
      this.peers,
      new Set(this.connections.map((c) => c.peerId)),
      {
        currentBase: this.profile.currentBase,
        skills: this.profile.skills,
        interests: this.profile.interests,
        industries: this.profile.industries,
      },
    ));
  }

  // ── Meetups ─────────────────────────────────────────────────────────────────

  createMeetup(input: NewMeetupInput, at: number = Date.now()): Meetup | null {
    if (!asString(input.title) || !asString(input.city)) return null;
    if (!MEETUP_KINDS.includes(input.kind)) return null;
    const meetup: Meetup = {
      ...input,
      title: input.title.trim(),
      city: input.city.trim(),
      venue: asString(input.venue),
      scheduledFor: asNumberOrNull(input.scheduledFor),
      attendeePeerIds: (input.attendeePeerIds ?? []).filter((id) => this.peers.some((p) => p.id === id)),
      attended: false,
      id: `meetup-${at}-${this.meetups.length}`,
      createdAt: at,
    };
    this.meetups.push(meetup);
    if (this.meetups.length > MAX_MEETUPS) this.meetups = this.meetups.slice(-MAX_MEETUPS);
    this.publishNetworkEvent('meetupCreated', { meetupId: meetup.id, meetupKind: meetup.kind, city: meetup.city }, at);
    this.afterUpdate();
    return structuredClone(meetup);
  }

  /** Marking attended feeds the reputation counter — once per meetup. */
  markMeetupAttended(id: string): boolean {
    const meetup = this.meetups.find((m) => m.id === id);
    if (!meetup || meetup.attended) return false;
    meetup.attended = true;
    this.afterUpdate();
    return true;
  }

  getMeetups(filter?: { city?: string; kind?: MeetupKind }): Meetup[] {
    let list = this.meetups;
    if (filter?.city) list = list.filter((m) => m.city === filter.city);
    if (filter?.kind) list = list.filter((m) => m.kind === filter.kind);
    return structuredClone(list);
  }

  // ── Collaboration ───────────────────────────────────────────────────────────

  createCollaboration(input: NewCollaborationInput, at: number = Date.now()): Collaboration | null {
    if (!asString(input.title) || !COLLABORATION_KINDS.includes(input.kind)) return null;
    if (!['proposed', 'active', 'completed', 'ended'].includes(input.status)) return null;
    const collaboration: Collaboration = {
      ...input,
      title: input.title.trim(),
      peerIds: (input.peerIds ?? []).filter((id) => this.peers.some((p) => p.id === id)),
      notes: input.notes ?? '',
      id: `collab-${at}-${this.collaborations.length}`,
      createdAt: at,
    };
    this.collaborations.push(collaboration);
    if (this.collaborations.length > MAX_COLLABORATIONS) {
      this.collaborations = this.collaborations.slice(-MAX_COLLABORATIONS);
    }
    this.afterUpdate();
    return structuredClone(collaboration);
  }

  updateCollaboration(id: string, patch: Partial<Pick<Collaboration, 'status' | 'notes' | 'title'>>): boolean {
    const collaboration = this.collaborations.find((c) => c.id === id);
    if (!collaboration) return false;
    if (patch.status != null && !['proposed', 'active', 'completed', 'ended'].includes(patch.status)) return false;
    Object.assign(collaboration, patch);
    this.afterUpdate();
    return true;
  }

  getCollaborations(kind?: CollaborationKind): Collaboration[] {
    const list = kind ? this.collaborations.filter((c) => c.kind === kind) : this.collaborations;
    return structuredClone(list);
  }

  // ── Local groups ────────────────────────────────────────────────────────────

  addGroup(input: NewGroupInput, at: number = Date.now()): LocalGroup | null {
    if (!asString(input.name) || !asString(input.city)) return null;
    const memberCount = asNumberOrNull(input.memberCount);
    const group: LocalGroup = {
      ...input,
      name: input.name.trim(),
      city: input.city.trim(),
      interests: asStringArray(input.interests),
      memberCount: memberCount != null && memberCount >= 0 ? Math.floor(memberCount) : null,
      joined: false,
      joinedAt: null,
      id: `group-${at}-${this.groups.length}`,
      addedAt: at,
    };
    this.groups.push(group);
    if (this.groups.length > MAX_GROUPS) this.groups = this.groups.slice(-MAX_GROUPS);
    this.afterUpdate();
    return structuredClone(group);
  }

  joinGroup(id: string, at: number = Date.now()): boolean {
    const group = this.groups.find((g) => g.id === id);
    if (!group || group.joined) return false;
    group.joined = true;
    group.joinedAt = at;
    this.publishNetworkEvent('groupJoined', { groupId: group.id, name: group.name, city: group.city }, at);
    this.afterUpdate();
    return true;
  }

  leaveGroup(id: string): boolean {
    const group = this.groups.find((g) => g.id === id);
    if (!group || !group.joined) return false;
    group.joined = false;
    group.joinedAt = null;
    this.afterUpdate();
    return true;
  }

  getGroups(filter?: { city?: string; joinedOnly?: boolean; interest?: string }): LocalGroup[] {
    let list = this.groups;
    if (filter?.city) list = list.filter((g) => g.city === filter.city);
    if (filter?.joinedOnly) list = list.filter((g) => g.joined);
    if (filter?.interest) {
      list = list.filter((g) => g.interests.some((i) => i.toLowerCase() === filter.interest!.toLowerCase()));
    }
    return structuredClone(list);
  }

  // ── Verification (future) & reputation ──────────────────────────────────────

  addVerificationIndicator(kind: VerificationIndicatorKind, evidence: string, at: number = Date.now()): VerificationIndicator | null {
    if (!VERIFICATION_INDICATOR_KINDS.includes(kind) || !asString(evidence)) return null;
    const indicator: VerificationIndicator = { kind, evidence: evidence.trim(), recordedAt: at };
    this.indicators.push(indicator);
    if (this.indicators.length > MAX_INDICATORS) this.indicators = this.indicators.slice(-MAX_INDICATORS);
    this.afterUpdate();
    return structuredClone(indicator);
  }

  getVerificationIndicators(): VerificationIndicator[] {
    return structuredClone(this.indicators);
  }

  /** A helpful report / community contribution the user actually made. */
  recordContribution(kind: 'helpfulReport' | 'contribution', at: number = Date.now()): void {
    if (kind === 'helpfulReport') this.helpfulReports += 1;
    else this.contributions += 1;
    this.publishNetworkEvent('communityContribution', { contributionKind: kind }, at);
    this.afterUpdate();
  }

  addRecommendation(fromPeerId: string, text: string, at: number = Date.now()): Recommendation | null {
    if (!this.peers.some((p) => p.id === fromPeerId) || !asString(text)) return null;
    const recommendation: Recommendation = {
      id: `rec-${at}-${this.recommendations.length}`,
      fromPeerId,
      text: text.trim(),
      recordedAt: at,
    };
    this.recommendations.push(recommendation);
    if (this.recommendations.length > MAX_RECOMMENDATIONS) {
      this.recommendations = this.recommendations.slice(-MAX_RECOMMENDATIONS);
    }
    this.afterUpdate();
    return structuredClone(recommendation);
  }

  getReputation(): CommunityReputation {
    const meetupsAttended = this.meetups.filter((m) => m.attended).length;
    const components = {
      helpfulReports: this.helpfulReports,
      meetupsAttended,
      contributions: this.contributions,
      missionParticipation: this.missionParticipation,
      recommendations: this.recommendations.length,
    };
    return { ...components, communityTrust: computeCommunityTrust(components) };
  }

  // ── Privacy, export, delete ─────────────────────────────────────────────────

  getPrivacySettings(): NetworkPrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<NetworkPrivacySettings>): NetworkPrivacySettings {
    this.privacy = { ...this.privacy, ...patch };
    this.afterUpdate();
    return this.getPrivacySettings();
  }

  exportNetwork(now: number = Date.now()): NetworkExport {
    let profile: NetworkProfile | null = null;
    if (this.privacy.visible) {
      profile = structuredClone(this.profile);
      if (!this.privacy.shareCurrentBase) profile.currentBase = null;
      if (!this.privacy.shareProfessionalDetails) {
        profile.professionalTitle = null;
        profile.skills = [];
        profile.industries = [];
        profile.portfolioLinks = [];
        profile.remoteExperienceYears = null;
      }
    }
    return structuredClone({
      exportedAt: now,
      profile,
      peers: this.peers,
      connections: this.connections,
      requests: this.requests,
      meetups: this.meetups,
      collaborations: this.collaborations,
      groups: this.groups,
      indicators: this.indicators,
      recommendations: this.recommendations,
    });
  }

  deleteAll(): void {
    this.profile = structuredClone(DEFAULT_NETWORK_PROFILE);
    this.privacy = { ...DEFAULT_NETWORK_PRIVACY };
    this.selfLatitude = null;
    this.selfLongitude = null;
    this.peers = [];
    this.connections = [];
    this.requests = [];
    this.meetups = [];
    this.collaborations = [];
    this.groups = [];
    this.indicators = [];
    this.recommendations = [];
    this.helpfulReports = 0;
    this.contributions = 0;
    this.missionParticipation = 0;
    this.refreshSignalCount = 0;
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        profile: this.profile,
        privacy: this.privacy,
        selfLatitude: this.selfLatitude,
        selfLongitude: this.selfLongitude,
        peers: this.peers,
        connections: this.connections,
        requests: this.requests,
        meetups: this.meetups,
        collaborations: this.collaborations,
        groups: this.groups,
        indicators: this.indicators,
        recommendations: this.recommendations,
        helpfulReports: this.helpfulReports,
        contributions: this.contributions,
        missionParticipation: this.missionParticipation,
        refreshSignalCount: this.refreshSignalCount,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('network', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Cloud hydration: reload in-memory state from localStorage after cloud adoption. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const pr = (state.profile ?? {}) as Record<string, unknown>;
      this.profile = {
        displayName: asString(pr.displayName),
        professionalTitle: asString(pr.professionalTitle),
        currentBase: asString(pr.currentBase),
        homeCountry: asString(pr.homeCountry),
        languages: asStringArray(pr.languages),
        skills: asStringArray(pr.skills),
        industries: asStringArray(pr.industries),
        portfolioLinks: asStringArray(pr.portfolioLinks),
        remoteExperienceYears: asNumberOrNull(pr.remoteExperienceYears),
        countriesVisited: asNumberOrNull(pr.countriesVisited),
        roamerLevel: asString(pr.roamerLevel),
        achievements: asStringArray(pr.achievements).slice(-MAX_ACHIEVEMENTS),
        availability: AVAILABILITIES.includes(pr.availability as NetworkAvailability)
          ? (pr.availability as NetworkAvailability) : 'unknown',
        interests: asStringArray(pr.interests),
        workStyle: pr.workStyle === 'async' || pr.workStyle === 'coworking' ||
          pr.workStyle === 'cafe' || pr.workStyle === 'mixed' ? pr.workStyle : null,
        // Verification is future-owned; only known values survive, default unverified.
        verificationStatus: pr.verificationStatus === 'verified' || pr.verificationStatus === 'pending'
          ? pr.verificationStatus : 'unverified',
      };

      const pv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        visible: pv.visible === true,
        shareCurrentBase: pv.shareCurrentBase === true,
        shareOnlineStatus: pv.shareOnlineStatus === true,
        shareProfessionalDetails: pv.shareProfessionalDetails === true,
        allowConnectionRequests: pv.allowConnectionRequests === true,
        meetupAvailability: pv.meetupAvailability === true,
        allowDirectMessages: pv.allowDirectMessages === true,
      };

      this.selfLatitude = asNumberOrNull(state.selfLatitude);
      this.selfLongitude = asNumberOrNull(state.selfLongitude);

      this.peers = (Array.isArray(state.peers) ? state.peers : [])
        .filter((p): p is Record<string, unknown> =>
          p != null && typeof p === 'object' &&
          typeof (p as { id?: unknown }).id === 'string' &&
          typeof (p as { displayName?: unknown }).displayName === 'string' &&
          (p as { displayName: string }).displayName.trim().length > 0)
        .map((p): PeerProfile => ({
          id: p.id as string,
          displayName: (p.displayName as string).trim(),
          professionalTitle: asString(p.professionalTitle),
          city: asString(p.city),
          country: asString(p.country),
          latitude: asNumberOrNull(p.latitude),
          longitude: asNumberOrNull(p.longitude),
          languages: asStringArray(p.languages),
          skills: asStringArray(p.skills),
          industries: asStringArray(p.industries),
          interests: asStringArray(p.interests),
          remoteExperienceYears: asNumberOrNull(p.remoteExperienceYears),
          availability: AVAILABILITIES.includes(p.availability as NetworkAvailability)
            ? (p.availability as NetworkAvailability) : 'unknown',
          verified: p.verified === true,
          addedAt: asNumberOrNull(p.addedAt) ?? 0,
        }))
        .slice(-MAX_PEERS);

      const peerIds = new Set(this.peers.map((p) => p.id));

      this.connections = (Array.isArray(state.connections) ? state.connections : [])
        .filter((c): c is Record<string, unknown> =>
          c != null && typeof c === 'object' &&
          typeof (c as { peerId?: unknown }).peerId === 'string' &&
          peerIds.has((c as { peerId: string }).peerId))
        .map((c): NetworkConnection => ({
          peerId: c.peerId as string,
          favorite: c.favorite === true,
          follower: c.follower === true,
          notes: typeof c.notes === 'string' ? c.notes : '',
          connectedAt: asNumberOrNull(c.connectedAt) ?? 0,
        }));

      this.requests = (Array.isArray(state.requests) ? state.requests : [])
        .filter((r): r is Record<string, unknown> =>
          r != null && typeof r === 'object' &&
          typeof (r as { id?: unknown }).id === 'string' &&
          typeof (r as { peerId?: unknown }).peerId === 'string' &&
          peerIds.has((r as { peerId: string }).peerId) &&
          ['pending', 'accepted', 'declined'].includes((r as { status?: never }).status as never) &&
          ['incoming', 'outgoing'].includes((r as { direction?: never }).direction as never))
        .map((r): ConnectionRequest => ({
          id: r.id as string,
          peerId: r.peerId as string,
          direction: r.direction as ConnectionRequest['direction'],
          message: typeof r.message === 'string' ? r.message : '',
          status: r.status as ConnectionRequest['status'],
          createdAt: asNumberOrNull(r.createdAt) ?? 0,
          resolvedAt: asNumberOrNull(r.resolvedAt),
        }))
        .slice(-MAX_REQUESTS);

      this.meetups = (Array.isArray(state.meetups) ? state.meetups : [])
        .filter((m): m is Record<string, unknown> =>
          m != null && typeof m === 'object' &&
          typeof (m as { id?: unknown }).id === 'string' &&
          typeof (m as { title?: unknown }).title === 'string' &&
          typeof (m as { city?: unknown }).city === 'string' &&
          MEETUP_KINDS.includes((m as { kind?: never }).kind as never))
        .map((m): Meetup => ({
          id: m.id as string,
          kind: m.kind as MeetupKind,
          title: m.title as string,
          city: m.city as string,
          venue: asString(m.venue),
          scheduledFor: asNumberOrNull(m.scheduledFor),
          attendeePeerIds: asStringArray(m.attendeePeerIds).filter((id) => peerIds.has(id)),
          attended: m.attended === true,
          createdAt: asNumberOrNull(m.createdAt) ?? 0,
        }))
        .slice(-MAX_MEETUPS);

      this.collaborations = (Array.isArray(state.collaborations) ? state.collaborations : [])
        .filter((c): c is Record<string, unknown> =>
          c != null && typeof c === 'object' &&
          typeof (c as { id?: unknown }).id === 'string' &&
          typeof (c as { title?: unknown }).title === 'string' &&
          COLLABORATION_KINDS.includes((c as { kind?: never }).kind as never) &&
          ['proposed', 'active', 'completed', 'ended'].includes((c as { status?: never }).status as never))
        .map((c): Collaboration => ({
          id: c.id as string,
          kind: c.kind as CollaborationKind,
          title: c.title as string,
          peerIds: asStringArray(c.peerIds).filter((id) => peerIds.has(id)),
          status: c.status as Collaboration['status'],
          notes: typeof c.notes === 'string' ? c.notes : '',
          createdAt: asNumberOrNull(c.createdAt) ?? 0,
        }))
        .slice(-MAX_COLLABORATIONS);

      this.groups = (Array.isArray(state.groups) ? state.groups : [])
        .filter((g): g is Record<string, unknown> =>
          g != null && typeof g === 'object' &&
          typeof (g as { id?: unknown }).id === 'string' &&
          typeof (g as { name?: unknown }).name === 'string' &&
          typeof (g as { city?: unknown }).city === 'string')
        .map((g): LocalGroup => {
          const memberCount = asNumberOrNull(g.memberCount);
          const joined = g.joined === true;
          return {
            id: g.id as string,
            name: g.name as string,
            city: g.city as string,
            interests: asStringArray(g.interests),
            memberCount: memberCount != null && memberCount >= 0 ? Math.floor(memberCount) : null,
            joined,
            joinedAt: joined ? asNumberOrNull(g.joinedAt) : null,
            addedAt: asNumberOrNull(g.addedAt) ?? 0,
          };
        })
        .slice(-MAX_GROUPS);

      this.indicators = (Array.isArray(state.indicators) ? state.indicators : [])
        .filter((i): i is VerificationIndicator =>
          i != null && typeof i === 'object' &&
          VERIFICATION_INDICATOR_KINDS.includes((i as VerificationIndicator).kind) &&
          typeof (i as VerificationIndicator).evidence === 'string' &&
          (i as VerificationIndicator).evidence.trim().length > 0)
        .slice(-MAX_INDICATORS);

      this.recommendations = (Array.isArray(state.recommendations) ? state.recommendations : [])
        .filter((r): r is Recommendation =>
          r != null && typeof r === 'object' &&
          typeof (r as Recommendation).id === 'string' &&
          typeof (r as Recommendation).fromPeerId === 'string' &&
          peerIds.has((r as Recommendation).fromPeerId) &&
          typeof (r as Recommendation).text === 'string' &&
          (r as Recommendation).text.trim().length > 0)
        .slice(-MAX_RECOMMENDATIONS);

      this.helpfulReports = asNonNegativeInt(state.helpfulReports);
      this.contributions = asNonNegativeInt(state.contributions);
      this.missionParticipation = asNonNegativeInt(state.missionParticipation);
      this.refreshSignalCount = asNonNegativeInt(state.refreshSignalCount);
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerNetworkModule = new RoamerNetworkModule();

/**
 * Boot hydration: adopt cloud Network state if strictly newer than local.
 * Called by HydrationOrchestrator. Returns true if cloud data was adopted.
 * If no cloud record exists yet, migrates current local data to cloud (first-run).
 */
export async function hydrateNetworkFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<PersistedState>('network', 'state');
  if (!cloud) {
    // Migrate: push existing local data to cloud on first run with cloud sync.
    try {
      if (typeof localStorage !== 'undefined') {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) storageManager.push('network', 'state', JSON.parse(raw) as PersistedState);
      }
    } catch { /* best-effort */ }
    return false;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cloud));
    }
    roamerNetworkModule.reloadFromStorage();
    return true;
  } catch { return false; }
}
